/**
 * Video — MP4 and WebM playback and metadata (§20).
 *
 * Video sits on the same §20 line as audio: the platform's `<video>` element
 * decodes and plays, and the app bundles no codec. It differs in one honest
 * respect — **there is no editing yet.** Trimming video losslessly means
 * rewriting a container's sample tables (MP4) or cutting on cluster boundaries
 * and rebuilding the cues (WebM), and neither ships. So this handler exposes no
 * `exportBytes` and the catalog rates these formats `read-only`: §19 forbids
 * advertising an edit that does not exist, and a viewer with no Edit button is
 * the truthful shape of "playback only".
 *
 * The metadata parsers touch no sample data, and whether a given file actually
 * plays is asked of the platform at runtime — the viewer calls `canPlayType`
 * and says plainly when the codec is missing, rather than presenting a player
 * that fails in silence.
 */
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import type {
  FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import { parseMp4 } from './mp4.js';
import { parseWebm } from './webm.js';

export interface VideoModel {
  readonly kind: 'video';
  readonly formatId: string;
  /** MIME type for the `<video>` element's source. */
  readonly mimeType: string;
  readonly durationSec: number;
  readonly width: number | null;
  readonly height: number | null;
  readonly videoCodec: string | null;
  readonly audioCodec: string | null;
  readonly hasAudio: boolean;
}

const MIME_BY_ID: Readonly<Record<string, string>> = {
  mp4: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm',
};

function readModel(source: FileSource): VideoModel {
  const mimeType = MIME_BY_ID[source.formatId] ?? 'application/octet-stream';

  if (source.formatId === 'webm') {
    const info = parseWebm(source.bytes);
    if (!info) throw new HandlerError('error.damagedOrIncomplete', 'not a readable WebM/Matroska file');
    return {
      kind: 'video', formatId: source.formatId, mimeType,
      durationSec: info.durationSec,
      width: info.width, height: info.height,
      videoCodec: info.videoCodec, audioCodec: info.audioCodec, hasAudio: info.hasAudio,
    };
  }

  const info = parseMp4(source.bytes);
  if (!info) throw new HandlerError('error.damagedOrIncomplete', 'not a readable MP4/QuickTime file');
  return {
    kind: 'video', formatId: source.formatId, mimeType,
    durationSec: info.durationSec,
    width: info.width, height: info.height,
    videoCodec: info.videoCodec, audioCodec: info.audioCodec, hasAudio: info.hasAudio,
  };
}

export const videoHandler: FormatHandler<VideoModel> = {
  id: 'video',
  formatIds: ['mp4', 'mov', 'webm'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const model = readModel(source);
    return {
      kind: 'media',
      unitCount: undefined,
      unitKey: undefined,
      firstContent: {
        kind: 'video',
        formatId: model.formatId,
        mimeType: model.mimeType,
        durationSec: model.durationSec,
        width: model.width,
        height: model.height,
        videoCodec: model.videoCodec,
        audioCodec: model.audioCodec,
      },
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<VideoModel>> {
    throwIfAborted(ctx.signal);
    const model = readModel(source);
    const spec = FORMATS_BY_ID.get(source.formatId);
    const warnings: HandlerWarning[] = [];

    warnings.push({
      messageKey: 'status.limitedSupport',
      severity: 'info',
      detail: 'Video is played by the platform’s own decoder. If this device lacks the codec, playback is declined rather than faked (§20). This build plays video but does not edit it.',
    });
    if (model.durationSec <= 0) {
      warnings.push({
        messageKey: 'error.featuresUnsupported',
        severity: 'degraded',
        detail: 'The video length could not be read; playback is still attempted through the platform decoder.',
      });
    }
    if (source.sizeBytes > 200 * 1024 * 1024) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'info',
        detail: `large video file (${formatFileSize('en', source.sizeBytes)})`,
      });
    }

    return {
      formatId: source.formatId,
      model,
      // Playback only — no editing ships for video, so it opens read-only.
      readOnly: true,
      editLevel: spec?.edit ?? 'read-only',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },

  // No exportBytes: video is not edited in this build (§19).
};

/** A display string for a video's shape — "1920×1080 · H.264" when known. */
export function videoHeadline(model: VideoModel): string {
  const parts: string[] = [];
  if (model.width && model.height) parts.push(`${model.width}×${model.height}`);
  if (model.videoCodec) parts.push(model.videoCodec);
  return parts.join(' · ');
}
